import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { CheckCircle, Close, PlaylistAdd, ReceiptLong, SwapVert } from '@mui/icons-material';
import { ParlayLeg, priceParlay, ProbUncertaintyBand, RaceChaos, SimModel, SimulationParlay } from '../services/api';
import { BetValuePanel } from './BetValuePanel';
import { ParlayGapBox } from './ParlayGapBox';
import { bookLegProb, parlayGap } from '../utils/bookMode';
import { addToSlip, betFromLegs, useSlipCount } from '../utils/slipStore';
import { SegmentedToggle } from './SegmentedToggle';
import { anyBandPts, parlayBandPts } from '../utils/betValue';
import { toAmericanOdds } from './OddsToggle';
import {
  legKey,
  legLabel,
  MAX_PARLAY_LEGS,
  PARLAY_FULL_MESSAGE,
  shortLegLabel,
} from '../utils/parlay';

const PARLAY_SIM_COUNTS = [20000, 50000];
const MAX_LEGS = MAX_PARLAY_LEGS;

const pct = (p: number, digits = 1) => `${(p * 100).toFixed(digits)}%`;

interface ParlaySlipProps {
  site: string;
  excludeTracks: string[];
  /** Drivers in the current run, for the matchup-leg form. */
  drivers: string[];
  legs: ParlayLeg[];
  onChange: (legs: ParlayLeg[]) => void;
  showOdds: boolean;
  /** The Simulator's model: production or the track-specific layer. */
  model?: SimModel;
  /** The run's race chaos scenario (the parlay is priced the same way). */
  chaos?: RaceChaos;
  /** Backtest uncertainty bands (for edge confidence in the value panel). */
  uncertainty?: ProbUncertaintyBand[];
  /** Rendered inside another card (the bet tools tabs): no frame or title of its own. */
  embedded?: boolean;
}

/**
 * Same-race parlay builder: legs are added from the results table (+ on Win / Top N cells) or as matchups here,
 * then priced together on the same simulated races, so the joint probability includes the correlation between legs.
 */
export const ParlaySlip: React.FC<ParlaySlipProps> = ({
  site,
  excludeTracks,
  drivers,
  legs,
  onChange,
  showOdds,
  model = 'base',
  uncertainty,
  embedded = false,
  chaos,
}) => {
  const [nSims, setNSims] = useState(PARLAY_SIM_COUNTS[0]);
  const [result, setResult] = useState<SimulationParlay | null>(null);
  const [pricedKey, setPricedKey] = useState<string | null>(null);
  const [isPricing, setIsPricing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [matchupDriver, setMatchupDriver] = useState<string | null>(null);
  const [matchupOthers, setMatchupOthers] = useState<string[]>([]);
  const [showMatchupForm, setShowMatchupForm] = useState(false);

  // The slip's identity: when legs, sims or the run's settings change, the shown price is out of date.
  const slipKey = useMemo(
    () => `${site}|${model}|${chaos ?? 'normal'}|${excludeTracks.join(',')}|${nSims}|${legs.map(legKey).join(';')}`,
    [site, model, chaos, excludeTracks, nSims, legs]
  );
  const isStale = result !== null && pricedKey !== slipKey;

  useEffect(() => {
    if (legs.length === 0) {
      setResult(null);
      setError(null);
    }
  }, [legs.length]);

  const handlePrice = async () => {
    setIsPricing(true);
    setError(null);
    const key = slipKey;
    try {
      const r = await priceParlay(site, legs, nSims, excludeTracks, model, chaos);
      setResult(r);
      setPricedKey(key);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to price parlay');
    } finally {
      setIsPricing(false);
    }
  };

  const removeLeg = (i: number) => onChange(legs.filter((_, j) => j !== i));
  // Flip a position leg between "top N" and "not top N" (a fade).
  const flipLeg = (i: number) =>
    onChange(
      legs.map((l, j) =>
        j === i && l.kind !== 'ahead_of' ? { ...l, kind: l.kind === 'top_n' ? 'not_top_n' : 'top_n' } : l
      )
    );

  const addMatchup = () => {
    if (!matchupDriver || matchupOthers.length === 0) return;
    const leg: ParlayLeg = { kind: 'ahead_of', driver: matchupDriver, others: matchupOthers };
    if (!legs.some((l) => legKey(l) === legKey(leg))) onChange([...legs, leg]);
    setMatchupDriver(null);
    setMatchupOthers([]);
    setShowMatchupForm(false);
  };

  const fmt = (p: number) => (showOdds ? toAmericanOdds(p) : pct(p));
  // Parlay: every leg hits. Any: at least one leg hits ("X top 10 or Y top 10"). Both come from the same pricing run.
  const [mode, setMode] = useState<'all' | 'any'>('all');
  // Older backends don't return anyProb: offer the toggle only when it's there.
  const anyAvailable = (result?.legs.length ?? 0) > 1 && typeof result?.anyProb === 'number';
  const slipCount = useSlipCount(site);
  // The unpriced parlay last added to the slip (by its legs), so the button shows it's been added.
  const [addedUnpriced, setAddedUnpriced] = useState<string | null>(null);
  const legsKey = JSON.stringify(legs);
  const anyMode = mode === 'any' && anyAvailable;
  const joint = (anyMode ? result?.anyProb : result?.jointProb) ?? 0;
  const jointSE = (anyMode ? result?.anySE : result?.jointSE) ?? 0;
  const independent = (anyMode ? result?.independentAnyProb : result?.independentProb) ?? 0;
  // Typical book prices for the legs and the parlay, and the gap split between them (every-leg parlays only).
  const legBooks = !result || anyMode ? [] : result.legs.map((l) => (l.missing ? null : bookLegProb(l)));
  const showBookCols = legBooks.some((b) => b !== null);
  const gap = result && !anyMode && !result.legs.some((l) => l.missing) ? parlayGap(result.legs, joint, independent) : null;
  // More than one driver's own finishing position involved: correlation between drivers isn't modeled.
  const multiDriver = new Set(legs.map((l) => l.driver)).size > 1;

  const Frame: React.ElementType = embedded ? Box : Paper;
  return (
    <Frame sx={{ p: embedded ? 0 : 2 }}>
      {(!embedded || legs.length > 0) && (
        <Stack direction="row" justifyContent={embedded ? 'flex-end' : 'space-between'} alignItems="center" sx={{ mb: 1 }}>
          {!embedded && (
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              Parlay builder{legs.length > 0 && ` (${legs.length})`}
            </Typography>
          )}
          {legs.length > 0 && (
            <Button size="small" onClick={() => onChange([])}>
              Clear
            </Button>
          )}
        </Stack>
      )}

      {legs.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Hover a Win / Top 3 / Top 5 / Top 10 number and click <strong>+</strong> to add it as a leg, or add a
          head-to-head below. Legs are priced together on the same simulated races, so the price includes how they
          move together.
        </Typography>
      ) : (
        <Stack spacing={0.5} sx={{ mb: 1 }}>
          {legs.map((l, i) => (
            <Stack key={legKey(l)} direction="row" alignItems="center" spacing={0.5}>
              <Typography variant="body2" sx={{ flexGrow: 1, minWidth: 0 }} noWrap title={legLabel(l)}>
                {legLabel(l)}
              </Typography>
              {l.kind !== 'ahead_of' && (
                <Tooltip title={l.kind === 'top_n' ? 'Switch to "not" (fade this driver)' : 'Switch back to top N'}>
                  <IconButton size="small" onClick={() => flipLeg(i)} aria-label="Flip leg" sx={{ p: 0.25 }}>
                    <SwapVert fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
              <IconButton size="small" onClick={() => removeLeg(i)} aria-label={`Remove ${legLabel(l)}`} sx={{ p: 0.25 }}>
                <Close fontSize="small" />
              </IconButton>
            </Stack>
          ))}
        </Stack>
      )}

      {showMatchupForm ? (
        <Stack spacing={1} sx={{ mb: 1.5 }}>
          <Autocomplete
            size="small"
            options={drivers}
            value={matchupDriver}
            onChange={(_, v) => setMatchupDriver(v)}
            renderInput={(params) => <TextField {...params} label="Driver" />}
          />
          <Autocomplete
            size="small"
            multiple
            options={drivers.filter((d) => d !== matchupDriver)}
            value={matchupOthers}
            onChange={(_, v) => setMatchupOthers(v.slice(0, 9))}
            renderInput={(params) => <TextField {...params} label="Finishes ahead of" />}
          />
          <Stack direction="row" spacing={1}>
            <Button
              size="small"
              variant="outlined"
              onClick={addMatchup}
              disabled={!matchupDriver || matchupOthers.length === 0 || legs.length >= MAX_LEGS}
            >
              Add leg
            </Button>
            <Button size="small" onClick={() => setShowMatchupForm(false)}>
              Cancel
            </Button>
          </Stack>
        </Stack>
      ) : (
        <Tooltip title={legs.length >= MAX_LEGS ? PARLAY_FULL_MESSAGE : ''}>
          <span>
            <Button
              size="small"
              startIcon={<PlaylistAdd />}
              onClick={() => setShowMatchupForm(true)}
              disabled={legs.length >= MAX_LEGS}
              sx={{ mb: 1 }}
            >
              Add matchup
            </Button>
          </span>
        </Tooltip>
      )}

      {legs.length > 0 && (
        // The race count and the button get their own rows so the button isn't squeezed in the narrow sidebar.
        <Stack spacing={1}>
          <Select
            size="small"
            fullWidth
            value={nSims}
            onChange={(e) => setNSims(Number(e.target.value))}
            disabled={isPricing}
            renderValue={(n) => `${Number(n).toLocaleString()} simulated races`}
          >
            {PARLAY_SIM_COUNTS.map((n) => (
              <MenuItem key={n} value={n}>
                {n.toLocaleString()} simulated races
              </MenuItem>
            ))}
          </Select>
          <Button
            variant="contained"
            fullWidth
            onClick={handlePrice}
            disabled={isPricing}
            startIcon={isPricing ? <CircularProgress size={16} color="inherit" /> : undefined}
            sx={{ py: 1, whiteSpace: 'nowrap' }}
          >
            {isPricing ? 'Pricing…' : result && !isStale ? 'Re-price' : 'Price parlay'}
          </Button>
        </Stack>
      )}
      {/* Not priced yet (or changed since): the parlay can still go straight onto the slip; the Slip Builder checks it.
          Once priced, the price check below has the button (with your odds and stake). */}
      {legs.length >= 2 && (!result || isStale) && !isPricing && (
        <Button
          fullWidth
          variant={addedUnpriced === legsKey ? 'text' : 'outlined'}
          startIcon={addedUnpriced === legsKey ? <CheckCircle /> : <ReceiptLong />}
          disabled={!betFromLegs(legs, '')}
          onClick={() => {
            const bet = betFromLegs(legs, '', '', mode === 'any');
            if (!bet || addedUnpriced === legsKey) return;
            addToSlip(site, bet);
            setAddedUnpriced(legsKey);
          }}
          sx={{ mt: 1 }}
        >
          {addedUnpriced === legsKey
            ? 'Added to Slip Builder'
            : betFromLegs(legs, '')
              ? 'Add to Slip Builder'
              : 'Can\'t add "outside the top N" legs to a slip yet'}
        </Button>
      )}
      {isPricing && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
          Simulating {nSims.toLocaleString()} races (about 10–20 seconds)…
        </Typography>
      )}
      {error && (
        <Alert severity="error" sx={{ mt: 1 }}>
          {error}
        </Alert>
      )}

      {result && legs.length > 0 && (
        <Box sx={{ mt: 2, opacity: isStale ? 0.5 : 1 }}>
          {isStale && (
            <Alert severity="info" sx={{ mb: 1, py: 0 }}>
              The slip changed. Re-price to update.
            </Alert>
          )}
          <Divider sx={{ mb: 1 }} />
          {anyAvailable && (
            <Box sx={{ mb: 1 }}>
              <SegmentedToggle
                aria-label="Parlay or any leg"
                fullWidth
                size="small"
                value={mode}
                onChange={setMode}
                options={[
                  { value: 'all', label: 'Parlay (all)', title: 'Every leg has to hit' },
                  { value: 'any', label: 'Any', title: 'At least one leg hits, e.g. "Larson or Hamlin top 10"' },
                ]}
              />
            </Box>
          )}
          <Table size="small" sx={{ '& .MuiTableCell-root': { px: 0.5 } }}>
            <TableHead>
              <TableRow>
                <TableCell>Leg</TableCell>
                <TableCell align="right">Alone</TableCell>
                {result.legs.length > 1 && !anyMode && (
                  <TableCell align="right">
                    <Tooltip title="How often this leg hits in the simulated races where every other leg hits">
                      <span>If others hit</span>
                    </Tooltip>
                  </TableCell>
                )}
                {showBookCols && (
                  <>
                    <TableCell align="right">
                      <Tooltip title="What a sportsbook would typically post for this leg on its own (book mode). Books don't usually post fades.">
                        <span>Typical book</span>
                      </Tooltip>
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="Our probability divided by the typical book price. Below ×1.00, books price the leg as more likely than we do, and in a parlay those gaps multiply.">
                        <span>Ours ÷ book</span>
                      </Tooltip>
                    </TableCell>
                  </>
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {result.legs.map((l, i) => (
                <TableRow key={legKey(l)}>
                  <TableCell sx={{ overflowWrap: 'anywhere' }} title={legLabel(l)}>
                    {shortLegLabel(l)}
                  </TableCell>
                  <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                    {fmt(l.prob)}
                  </TableCell>
                  {result.legs.length > 1 && !anyMode && (
                    <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                      {l.probGivenOthers === null ? '–' : fmt(l.probGivenOthers)}
                    </TableCell>
                  )}
                  {showBookCols && (
                    <>
                      <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                        {legBooks[i] ? fmt(legBooks[i]!) : '–'}
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{
                          fontVariantNumeric: 'tabular-nums',
                          color: !legBooks[i] ? undefined : l.prob >= legBooks[i]! ? 'success.main' : 'error.main',
                        }}
                      >
                        {legBooks[i] ? `×${(l.prob / legBooks[i]!).toFixed(2)}` : '–'}
                      </TableCell>
                    </>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>

          {result.legs.some((l) => l.missing) && (
            <Alert severity="warning" sx={{ mt: 1 }}>
              A driver in this slip isn't in the simulated field, so that leg can't hit.
            </Alert>
          )}

          <Box sx={{ mt: 1.5 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="baseline">
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {result.legs.length === 1 ? 'Leg hits' : anyMode ? 'Any leg hits' : 'All legs hit'}
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
                {pct(joint)}{' '}
                <Typography component="span" variant="caption" color="text.secondary">
                  ± {(jointSE * 100).toFixed(1)}
                </Typography>
              </Typography>
            </Stack>
            <Stack direction="row" justifyContent="space-between">
              <Typography variant="body2" color="text.secondary">
                Fair odds
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
                {joint > 0 ? toAmericanOdds(joint) : 'never hit'}
              </Typography>
            </Stack>
            {result.legs.length > 1 && (
              <>
                <Stack direction="row" justifyContent="space-between">
                  <Tooltip
                    title={
                      anyMode
                        ? "1 minus the chance every leg misses, from the legs' own probabilities, as if they had nothing to do with each other"
                        : "Multiplying the legs' own probabilities, as if they had nothing to do with each other"
                    }
                  >
                    <Typography variant="body2" color="text.secondary">
                      If independent
                    </Typography>
                  </Tooltip>
                  <Typography variant="body2" color="text.secondary" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                    {pct(independent)} ({independent > 0 && independent < 1 ? toAmericanOdds(independent) : '–'})
                  </Typography>
                </Stack>
                {independent > 0 && joint > 0 && (
                  <Chip
                    size="small"
                    variant="outlined"
                    sx={{ mt: 0.75 }}
                    label={
                      joint >= independent
                        ? `${(joint / independent).toFixed(2)}× more likely than independent`
                        : `${(independent / joint).toFixed(2)}× less likely than independent`
                    }
                  />
                )}
              </>
            )}
            {joint > 0 && joint * result.nSims < 50 && (
              <Typography variant="caption" color="warning.main" sx={{ display: 'block', mt: 0.5 }}>
                Hit in only {Math.round(joint * result.nSims)} of {result.nSims.toLocaleString()} simulated races; the
                price is rough. Try 50,000 races.
              </Typography>
            )}
          </Box>

          {joint > 0 && joint < 1 && (
            <Box sx={{ mt: 2 }}>
              <BetValuePanel
                key={mode}
                prob={joint}
                // "Any leg hits" isn't a bet the Slip Builder can hold; a parlay (all legs) is.
                // "Any" mode adds an "any leg hits" parlay; every-leg mode a normal one.
                onAddToSlip={
                  !betFromLegs(result.legs, '')
                    ? undefined
                    : (oddsText, stakeText) => {
                        const bet = betFromLegs(result.legs, oddsText, stakeText, anyMode);
                        if (bet) addToSlip(site, bet);
                        return !!bet;
                      }
                }
                // Bets the Slip Builder can't hold yet get a disabled button with the reason, rather than no button.
                addDisabledReason={
                  !betFromLegs(result.legs, '') ? 'The Slip Builder can\'t hold "outside the top N" legs yet.' : undefined
                }
                slipCount={slipCount}
                typicalBook={gap?.typical}
                renderGap={
                  gap && result.legs.length > 1
                    ? (implied) => (
                        <ParlayGapBox gap={gap} legLabels={result.legs.map(shortLegLabel)} implied={implied} joint={joint} />
                      )
                    : undefined
                }
                bandPts={
                  anyMode
                    ? anyBandPts(uncertainty, result.legs, joint, jointSE)
                    : parlayBandPts(uncertainty, result.legs, joint, jointSE)
                }
              />
            </Box>
          )}

          {multiDriver && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
              Different drivers are simulated independently, so real-world links between them (teammates, multi-car
              wrecks, cautions) aren't in this price; only finishing-order effects are (one winner, ten top-10 spots).
            </Typography>
          )}
        </Box>
      )}
    </Frame>
  );
};
